import { Box, Button, Loader, Modal, Title, UnstyledButton } from '@mantine/core';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';

import { Declaration, Document, TaricOtherMeasures } from '@e-origin/shared';

import { closeIcon } from '@assets';
import { ManageSupportingDocumentsRow, ManageSupportingDocumentsTable } from '@components';
import { classes } from '@styles';
import { useDeclarationService } from '../../../services';

interface ManageSupportingDocumentsProps {
  opened: boolean;
  documents: Document[];
  declaration: Declaration;
  taricOtherMeasures: TaricOtherMeasures[];
  onHide(): void;
}

export const ManageSupportingDocuments: React.FC<ManageSupportingDocumentsProps> = (props) => {
  const [rows, setRows] = useState<ManageSupportingDocumentsRow[]>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { saveDeclarationDetails } = useDeclarationService();

  const extractCodesFromTaricOtherMeasures = (taricOtherMeasures: TaricOtherMeasures[]) => {
    const codes: string[] = [];
    taricOtherMeasures.forEach((measure) => {
      if (!measure.measureType.toLowerCase().includes('import') || !measure.duty) {
        return;
      }

      codes.push(...measure.duty.match(/[A-Z]-[0-9]+/g).map((code) => code.replace('-', '')));
    });

    return codes;
  };

  useEffect(() => {
    if (props.documents) {
      const supportingDocuments = cloneDeep(props.documents);
      const additionalCodes = extractCodesFromTaricOtherMeasures(props.taricOtherMeasures);

      const additionalDocuments = additionalCodes
        .map((code) => {
          if (supportingDocuments.find((doc) => doc.type === code)) {
            return undefined;
          }

          return {
            type: code,
            referenceNumber: '.',
            dateOfValidity: undefined,
          };
        })
        .filter((doc) => !!doc);

      setRows([
        ...supportingDocuments.map((doc) => ({ selected: true, document: doc })),
        ...additionalDocuments.map((doc) => ({ selected: false, document: doc })),
      ]);
    }
  }, [props.documents]);

  const save = async () => {
    setIsSaving(true);
    await saveDeclarationDetails({
      updatedDeclaration: {
        ...props.declaration,
        documents: {
          ...props.declaration.documents,
          supportingDocuments: rows.filter((d) => d.selected).map((d) => d.document),
        },
      },
      propertiesToSend: ['documents'],
    });
    setIsSaving(false);
    props.onHide();
  };

  const handleTableUpdate = (data: ManageSupportingDocumentsRow[]) => {
    setRows(data);
  };

  return (
    <Modal size={800} opened={props.opened} onClose={props.onHide} zIndex={999} centered withCloseButton={false}>
      <Box className={classes.header}>
        <Title order={4} fz={18} fw={600}>
          Manage supporting documents
        </Title>
        <div>
          <UnstyledButton onClick={props.onHide}>
            <img src={closeIcon} alt="" />
          </UnstyledButton>
        </div>
      </Box>
      <Box p={15}>
        <ManageSupportingDocumentsTable documents={rows} onChange={handleTableUpdate} />
      </Box>
      <Box className={classes.footer}>
        <Button type="button" onClick={props.onHide} size="lg" variant="transparent">
          Cancel
        </Button>
        <Button type="button" onClick={save} variant="filled" size="lg">
          {isSaving ? (
            <>
              <Loader color="#FFFFFF" />
              Saving
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Box>
    </Modal>
  );
};
